import { useEffect, useState } from 'react';
import Head from 'next/head';
import { useRouter } from 'next/router';
import Skeleton from 'react-loading-skeleton';
import { useAuth0User } from '@hooks/useAuth0User';

import { useLeagueContext } from '@hooks/useLeagueContext';
import Layout from '@components/layout';
import { isPaidMember } from '@lib/utils';
import { buildHomepageJsonLd, getPageSeoHead, PAGE_SEO } from '@lib/page-seo';
import HomepageHero from '@components/homepage/homepage-hero';
import ReviewsCarousel from '@components/reviews-carousel';
import FeaturedArticle from '@components/featured-article';
import { LatestArticles } from '@components/homepage/latest-articles';
import ToolsShowcase from '@components/homepage/tools-showcase';
import VideoAdvert from '@components/video-advert';
import {
  getHomepageSettings,
  getLatestListingCards,
  getMarketingCopy
} from '@lib/sanity';
import type { MarketingCopy } from '@lib/sanity';
import { reportIsrRegenerationFailure } from '@lib/isr/report-regeneration-failure';
import { homepageToolsData } from '@components/homepage/tools-showcase/utils';
import {
  HOMEPAGE_SOURCE_LIMIT,
  buildHomepageListing
} from '@lib/homepage/build';
import type { HomepageListing } from '@lib/homepage/build';
import { cardLabelFor } from '@lib/homepage/tabs';
import type { HomepageTab } from '@lib/homepage/tabs';

const REVALIDATE_SECONDS = 300;

type HomeProps = HomepageListing & {
  heading: string;
  tabs: HomepageTab[];
  cardCount: number;
  marketingCopy: MarketingCopy;
};

const Home = ({
  lead,
  items,
  heading,
  tabs,
  cardCount,
  marketingCopy
}: HomeProps) => {
  const { context } = useLeagueContext();

  const nextGameweek = context?.nextGameweek?.number ?? null;
  const [isPayingMember, setIsPayingMember] = useState(false);
  const { user, isLoading } = useAuth0User();

  useEffect(() => {
    const hasPaidMembership = isPaidMember(user);

    if (hasPaidMembership) {
      setIsPayingMember(true);
    }
  }, [user]);

  const router = useRouter();
  const pathname = router.pathname;
  const pageHead = [
    ...getPageSeoHead(pathname, PAGE_SEO.home),
    <script
      type="application/ld+json"
      key="homepage-jsonld"
      dangerouslySetInnerHTML={{ __html: buildHomepageJsonLd() }}
    />
  ];

  if (isLoading) {
    // This branch is what gets statically generated (Auth0 is always loading
    // server-side), so the SEO head must render here or crawlers never see it.
    return (
      <>
        <Head>{pageHead}</Head>
        <div className="py-10 h-[95vh]">
          <Skeleton count={12} width={'100%'} />
        </div>
      </>
    );
  }

  return (
    <>
      <div>
        <Head>{pageHead}</Head>
        {!isPayingMember && (
          <>
            <div className="container mx-auto px-5">
              <HomepageHero copy={marketingCopy.homepage} />
            </div>
            <ReviewsCarousel />
            <ToolsShowcase
              toolsData={homepageToolsData}
              imagePath={'/images/homepage/tools-showcase.png'}
            />
            <VideoAdvert variant="image" />
          </>
        )}

        <FeaturedArticle
          nextGameweek={nextGameweek}
          item={lead}
          label={lead ? cardLabelFor(lead, tabs) : undefined}
        />
        <Layout hasRightSidebar={isPayingMember} content={false}>
          <LatestArticles
            heading={heading}
            items={items}
            tabs={tabs}
            cardCount={cardCount}
          />
        </Layout>
      </div>
    </>
  );
};

export const getStaticProps = async () => {
  try {
    const [sanityCards, settings, marketingCopy] = await Promise.all([
      getLatestListingCards(HOMEPAGE_SOURCE_LIMIT),
      getHomepageSettings(),
      getMarketingCopy()
    ]);

    const listing = buildHomepageListing({
      sanityCards,
      leadStory: settings.leadStory
    });

    return {
      props: {
        ...listing,
        heading: settings.heading,
        tabs: settings.tabs,
        cardCount: settings.cardCount,
        marketingCopy
      },
      revalidate: REVALIDATE_SECONDS
    };
  } catch (error) {
    return reportIsrRegenerationFailure('/', error);
  }
};

export default Home;
